import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { Gig, Call, Position, Booking } from '@gigdriver/domain';
import {
  createGig as createGigApi,
  getGig,
  updateGig as updateGigApi,
  getOrgGigs,
  deleteGig as deleteGigApi,
  createCall as createCallApi,
  getCall,
  updateCall as updateCallApi,
  getGigCalls,
  deleteCall as deleteCallApi,
  createPosition as createPositionApi,
  getPosition,
  updatePosition as updatePositionApi,
  getGigPositions,
  deletePosition as deletePositionApi,
  setPositionCallIds as setPositionCallIdsApi,
  watchGigDetail,
  duplicateGig as duplicateGigApi,
  refreshGigStaffing as refreshGigStaffingApi,
  type DuplicateGigOptions,
} from '@gigdriver/client';

export const useGigsStore = defineStore('gigs', () => {
  const gigs = ref<Map<string, Gig>>(new Map());
  const currentGig = ref<Gig | null>(null);
  const calls = ref<Map<string, Call>>(new Map());
  const positions = ref<Map<string, Position>>(new Map());
  const bookings = ref<Map<string, Booking>>(new Map());
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const gigList = computed(() => Array.from(gigs.value.values()));

  const bookingsByPosition = computed(() => {
    const grouped: Record<string, Booking[]> = {};
    for (const booking of bookings.value.values()) {
      (grouped[booking.positionId] ??= []).push(booking);
    }
    return grouped;
  });

  // Real-time subscription for the gig detail view. Writes snapshots into the
  // shared Maps; `stopGigDetail` tears down the whole listener tree.
  let stopGigDetail: (() => void) | null = null;

  function subscribeGigDetail(orgId: string, gigId: string, options: { skipBookings?: boolean } = {}) {
    unsubscribeGigDetail();
    error.value = null;
    // calls/positions/bookings are shared Maps with no gigId filter on read
    // (see the `calls`/`positions` computeds below) — the per-snapshot diff
    // logic below only ever prunes entries for the gig CURRENTLY being
    // watched, so without this, switching straight from one gig's detail
    // view to another's left the previous gig's calls/positions/bookings
    // sitting in the Maps and visible on the new gig's page until a full
    // page reload reset everything.
    currentGig.value = null;
    calls.value.clear();
    positions.value.clear();
    bookings.value.clear();
    stopGigDetail = watchGigDetail(orgId, gigId, {
      gig: g => {
        if (g) {
          gigs.value.set(g.id, g);
          currentGig.value = g;
        } else {
          currentGig.value = null;
        }
      },
      calls: list => {
        const seen = new Set(list.map(c => c.id));
        for (const id of [...calls.value.keys()]) {
          if (calls.value.get(id)?.gigId === gigId && !seen.has(id)) calls.value.delete(id);
        }
        list.forEach(c => calls.value.set(c.id, c));
      },
      positions: list => {
        const seen = new Set(list.map(p => p.id));
        for (const id of [...positions.value.keys()]) {
          if (positions.value.get(id)?.gigId === gigId && !seen.has(id)) positions.value.delete(id);
        }
        list.forEach(p => positions.value.set(p.id, p));
      },
      bookings: (positionId, list) => {
        const seen = new Set(list.map(b => b.id));
        for (const id of [...bookings.value.keys()]) {
          if (bookings.value.get(id)?.positionId === positionId && !seen.has(id)) {
            bookings.value.delete(id);
          }
        }
        list.forEach(b => bookings.value.set(b.id, b));
      },
      error: (ctx, err) => {
        error.value = err instanceof Error ? `${ctx}: ${err.message}` : `Failed to load ${ctx}`;
      },
    }, options);
  }

  function unsubscribeGigDetail() {
    stopGigDetail?.();
    stopGigDetail = null;
  }

  async function loadOrgGigs(orgId: string) {
    try {
      isLoading.value = true;
      error.value = null;
      const loaded = await getOrgGigs(orgId);
      gigs.value.clear();
      loaded.forEach(g => gigs.value.set(g.id, g));
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load gigs';
    } finally {
      isLoading.value = false;
    }
  }

  // One-time backfill of Gig.staffing for gigs that predate it; the trigger
  // keeps it current afterwards. Best-effort — the column just shows "—" for
  // a gig without a summary, so a failure here isn't surfaced as an error.
  async function backfillStaffing(orgId: string) {
    if ([...gigs.value.values()].every(g => g.staffing)) return;
    try {
      const { refreshed } = await refreshGigStaffingApi({ orgId });
      if (refreshed > 0) {
        const loaded = await getOrgGigs(orgId);
        loaded.forEach(g => gigs.value.set(g.id, g));
      }
    } catch (err) {
      console.warn('Gig staffing backfill failed:', err);
    }
  }

  async function createGig(orgId: string, gigData: Omit<Gig, 'id' | 'createdAt' | 'updatedAt' | 'calls'>) {
    try {
      isLoading.value = true;
      error.value = null;
      const gigId = await createGigApi(orgId, gigData);
      const gig = await getGig(orgId, gigId);
      if (gig) {
        gigs.value.set(gigId, gig);
        currentGig.value = gig;
      }
      return gigId;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to create gig';
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  // Copies a gig onto a new date (see duplicateGig in @gigdriver/client).
  // Doesn't touch isLoading/currentGig — the caller navigates to the copy,
  // whose detail page loads it itself.
  async function duplicateGig(orgId: string, sourceGigId: string, options: DuplicateGigOptions) {
    try {
      error.value = null;
      const gigId = await duplicateGigApi(orgId, sourceGigId, options);
      const gig = await getGig(orgId, gigId);
      if (gig) gigs.value.set(gigId, gig);
      return gigId;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to duplicate gig';
      throw err;
    }
  }

  async function selectGig(orgId: string, gigId: string) {
    try {
      isLoading.value = true;
      error.value = null;
      const gig = await getGig(orgId, gigId);
      if (gig) {
        currentGig.value = gig;
        gigs.value.set(gigId, gig);
        // Load calls for this gig
        await loadGigCalls(orgId, gigId);
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load gig';
    } finally {
      isLoading.value = false;
    }
  }

  async function updateGig(orgId: string, gigId: string, updates: Partial<Gig>) {
    try {
      error.value = null;
      await updateGigApi(orgId, gigId, updates);
      const gig = await getGig(orgId, gigId);
      if (gig) {
        gigs.value.set(gigId, gig);
        if (currentGig.value?.id === gigId) {
          currentGig.value = gig;
        }
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update gig';
      throw err;
    }
  }

  async function deleteGig(orgId: string, gigId: string) {
    try {
      error.value = null;
      await deleteGigApi(orgId, gigId);
      gigs.value.delete(gigId);
      if (currentGig.value?.id === gigId) {
        currentGig.value = null;
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete gig';
      throw err;
    }
  }

  // Call operations
  async function loadGigCalls(orgId: string, gigId: string) {
    try {
      const loaded = await getGigCalls(orgId, gigId);
      loaded.forEach(c => calls.value.set(c.id, c));
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load calls';
    }
  }

  async function createCall(orgId: string, gigId: string, callData: Omit<Call, 'id' | 'createdAt' | 'updatedAt' | 'positions'>) {
    try {
      isLoading.value = true;
      error.value = null;
      const callId = await createCallApi(orgId, gigId, callData);
      const call = await getCall(orgId, gigId, callId);
      if (call) {
        calls.value.set(callId, call);
      }
      return callId;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to create call';
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function updateCall(orgId: string, gigId: string, callId: string, updates: Partial<Call>) {
    try {
      error.value = null;
      await updateCallApi(orgId, gigId, callId, updates);
      const call = await getCall(orgId, gigId, callId);
      if (call) {
        calls.value.set(callId, call);
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update call';
      throw err;
    }
  }

  async function deleteCall(orgId: string, gigId: string, callId: string) {
    try {
      error.value = null;
      await deleteCallApi(orgId, gigId, callId);
      calls.value.delete(callId);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete call';
      throw err;
    }
  }

  // Position operations — a Position lives at the gig level now (a sibling
  // of `calls`, not a child of one).
  async function loadGigPositions(orgId: string, gigId: string) {
    try {
      const loaded = await getGigPositions(orgId, gigId);
      loaded.forEach(p => positions.value.set(p.id, p));
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load positions';
    }
  }

  async function createPosition(
    orgId: string,
    gigId: string,
    posData: Omit<Position, 'id' | 'createdAt' | 'updatedAt' | 'bookings'>,
  ) {
    try {
      isLoading.value = true;
      error.value = null;
      const posId = await createPositionApi(orgId, gigId, posData);
      const pos = await getPosition(orgId, gigId, posId);
      if (pos) {
        positions.value.set(posId, pos);
      }
      return posId;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to create position';
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function updatePosition(orgId: string, gigId: string, posId: string, updates: Partial<Position>) {
    try {
      error.value = null;
      await updatePositionApi(orgId, gigId, posId, updates);
      const pos = await getPosition(orgId, gigId, posId);
      if (pos) {
        positions.value.set(posId, pos);
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update position';
      throw err;
    }
  }

  async function setPositionCallIds(orgId: string, gigId: string, posId: string, callIds: string[]) {
    try {
      error.value = null;
      await setPositionCallIdsApi(orgId, gigId, posId, callIds);
      const pos = await getPosition(orgId, gigId, posId);
      if (pos) {
        positions.value.set(posId, pos);
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update linked calls';
      throw err;
    }
  }

  async function deletePosition(orgId: string, gigId: string, posId: string) {
    try {
      error.value = null;
      await deletePositionApi(orgId, gigId, posId);
      positions.value.delete(posId);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete position';
      throw err;
    }
  }

  return {
    gigs: gigList,
    currentGig,
    calls: computed(() =>
      Array.from(calls.value.values()).sort((a, b) => a.startTime.getTime() - b.startTime.getTime()),
    ),
    positions: computed(() => Array.from(positions.value.values())),
    bookingsByPosition,
    isLoading,
    error,
    loadOrgGigs,
    backfillStaffing,
    createGig,
    duplicateGig,
    selectGig,
    updateGig,
    deleteGig,
    loadGigCalls,
    createCall,
    updateCall,
    deleteCall,
    loadGigPositions,
    createPosition,
    updatePosition,
    setPositionCallIds,
    deletePosition,
    subscribeGigDetail,
    unsubscribeGigDetail,
  };
});
